import React, { useEffect, useState } from "react";
import { Users, UserCheck, DollarSign } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { getDashboardInfluenceur } from "../../services/InfluenceurService";
import * as LoadingIcons from "react-loading-icons";

// Format nombres
const formatNumber = (num) => {
    if (typeof num === "string") return num;
    if (!num) return 0;
    if (num < 1000) return num;
    if (num < 1000000) return (num / 1000).toFixed(num % 1000 >= 100 ? 1 : 0) + "K";
    if (num < 1000000000) return (num / 1000000).toFixed(1) + "M";
    return (num / 1000000000).toFixed(1) + "B";
};

const DashboardInfluenceur = () => {
    const [stats, setStats] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboard = async () => {
            try {
                const data = await getDashboardInfluenceur();

                // KPI stats
                const kpiStats = [
                    { title: "Clics", value: data.stats?.clics || 0, icon: Users, color: "bg-blue-100 text-blue-600" },
                    { title: "Inscriptions", value: data.stats?.inscriptions || 0, icon: Users, color: "bg-green-100 text-green-600" },
                    { title: "Abonnés actifs", value: data.stats?.abonnes || 0, icon: UserCheck, color: "bg-purple-100 text-purple-600" },
                    { title: "Commissions (€)", value: data.stats?.commissions || "0,00", icon: DollarSign, color: "bg-yellow-100 text-yellow-600" },
                ];

                setStats(kpiStats);

                // Graphe 12 mois
                const defaultChartData = [
                    { name: "Jan", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Fév", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Mar", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Avr", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Mai", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Juin", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Juil", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Août", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Sep", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Oct", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Nov", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Déc", inscrits: 0, abonnes: 0, commissions: 0 },
                ];

                setChartData(data.chartData?.length ? data.chartData : defaultChartData);

            } catch (err) {
                console.error("Erreur récupération dashboard:", err);
                setStats([
                    { title: "Clics", value: 0, icon: Users, color: "bg-blue-100 text-blue-600" },
                    { title: "Inscriptions", value: 0, icon: Users, color: "bg-green-100 text-green-600" },
                    { title: "Abonnés actifs", value: 0, icon: UserCheck, color: "bg-purple-100 text-purple-600" },
                    { title: "Commissions (€)", value: "0,00", icon: DollarSign, color: "bg-yellow-100 text-yellow-600" },
                ]);
                setChartData([
                    { name: "Jan", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Fév", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Mar", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Avr", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Mai", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Juin", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Juil", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Août", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Sep", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Oct", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Nov", inscrits: 0, abonnes: 0, commissions: 0 },
                    { name: "Déc", inscrits: 0, abonnes: 0, commissions: 0 },
                ]);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboard();
    }, []);
    if (loading) return (
        <div className="flex justify-center items-center h-screen">
            <LoadingIcons.SpinningCircles
                stroke="#2218eeff"
                fill="#2218eeff"
                strokeWidth={3}
                height={40}
                width={40}
            />
            <span className="ml-3 text-gray-600 font-medium">
                Chargement ...
            </span>
        </div>
    );

    return (
        <div className="w-full flex justify-center pb-20 lg:mb-0 mb-20 font-Poppins">
            <div className="w-full max-w-6xl space-y-6">
                <h1 className="text-2xl lg:text-3xl font-extrabold text-start text-color6 mb-6">
                    📊 Tableau de bord
                </h1>
                {/* KPIs */}
                {/* KPIs */}
                {/* KPIs */}
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
                    {stats.map((stat, idx) => (
                        <div
                            key={idx}
                            className="flex flex-col items-center justify-between bg-white shadow-lg rounded-2xl p-2 lg:p-3"
                        >
                            <div className={`p-2 lg:p-3 rounded-full ${stat.color}`}>
                                <stat.icon size={20} className="xl:size-10 lg:size-10" />
                            </div>
                            <div className="text-center mt-1">
                                <p className="text-xs lg:text-xl text-gray-800 font-semibold lg:font-semibold">
                                    {stat.title}
                                </p>
                                <h3 className="text-sm lg:text-3xl font-extrabold font-atkinson">
                                    {formatNumber(stat.value)}
                                </h3>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Graphique */}
                <div className="bg-white shadow-lg rounded-2xl lg:p-8 p-0 mb-10 w-full">
                    <h2 className="text-lg lg:text-2xl font-semibold mb-6">Évolution des performances</h2>
                    <div className="w-full h-80 lg:h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                <XAxis dataKey="name" tick={{ fontSize: 14 }} />
                                <YAxis tick={{ fontSize: 14 }} />
                                <Tooltip contentStyle={{ fontSize: 16 }} />
                                <Line type="monotone" dataKey="inscrits" stroke="#22c55e" strokeWidth={5} />
                                <Line type="monotone" dataKey="abonnes" stroke="#a855f7" strokeWidth={5} />
                                <Line type="monotone" dataKey="commissions" stroke="#f59e0b" strokeWidth={5} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default DashboardInfluenceur;
